import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Ambulance, LogOut, Wifi, WifiOff, Settings, ExternalLink, History } from "lucide-react";
import "leaflet/dist/leaflet.css";
import { UserModal, AgencyModal, HospitalModal, UnitModal, CallTypesModal, FireCallTypesModal, CallTypeMappingsModal, CSLModal, KMLBoundaryModal, UnitHistoryModal, FireMDTBindingsModal, ForceLogoffModal, CadCountersModal } from "@/pages/AdminModals";
import ReportsModal from "@/pages/ReportsModal";
import { GPSDevicesModal } from "@/components/gps/GPSDevicesModal";
import { mergeGps } from "@/components/gps/gpsUtils";
import { useWebSocket, CADEntry, DispatchBoard, ClassroomScreen, GPSMapScreen, VACRCCTab, API, localHMS } from "@/components/dispatch";
import MessageBoard from "@/components/dispatch/MessageBoard";
import { DiversionsTab } from "@/components/dispatch/DiversionsTab";

// Map URL sub-path → tab id.
const PATH_TO_TAB = { "": "cad", cad: "cad", board: "board", classroom: "classroom", gps: "gps", vacrcc: "vacrcc", messages: "messages", diversions: "diversions" };
const TAB_TO_PATH = { cad: "cad", board: "board", classroom: "classroom", gps: "gps", vacrcc: "vacrcc", messages: "messages", diversions: "diversions" };

/**
 * TabLink renders an absolute-URL <a href> so browsers offer the native
 * "Open Link in New Tab / Window" right-click menu, and middle-click /
 * Ctrl-Cmd-click open in new tab. The absolute URL is critical when the
 * app is embedded in an iframe (like Emergent's preview) because a relative
 * href would otherwise resolve against the parent origin when popped out
 * to a new tab.
 *
 * Plain left-click is intercepted for in-app SPA navigation so the current
 * tab's WebSocket connection stays alive.
 */
function TabLink({ tabId, active, children, onActivate, blink }) {
  // Absolute URL based on the current window — works both inside the Emergent
  // preview iframe and in a standalone browser tab.
  const href = `${window.location.origin}/dispatcher/${TAB_TO_PATH[tabId]}`;
  return (
    <div className={`nav-tab-wrap ${active ? "active" : ""} ${blink ? "blink-red" : ""}`} data-testid={`tab-wrap-${tabId}`}>
      <a
        data-testid={`tab-${tabId}`}
        href={href}
        className={`nav-tab ${active ? "active" : ""} ${blink ? "blink-red" : ""}`}
        onClick={(e) => {
          // Let the browser own modifier-clicks and middle-clicks so native
          // "open in new tab" behavior works.
          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
          e.preventDefault();
          onActivate(tabId);
        }}
        style={{ textDecoration: "none", color: "inherit" }}
      >
        {children}
      </a>
      {/* Pop-out icon: single left-click opens this view in a brand-new browser tab.
          Guaranteed to work inside Emergent's preview iframe (where right-click is
          often hidden by the parent frame). window.open with target _blank + noopener
          breaks out of the iframe to a real top-level tab. */}
      <button
        data-testid={`tab-popout-${tabId}`}
        className="nav-tab-popout"
        title={`Open ${TAB_TO_PATH[tabId].toUpperCase()} in a new browser tab`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          window.open(href, "_blank", "noopener,noreferrer");
        }}
      >
        <ExternalLink size={12} strokeWidth={2.5} />
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   DISPATCHER CONSOLE
   ═══════════════════════════════════════════════ */
export default function DispatcherConsole({ user, onLogout }) {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  // Derive the active tab from the URL so each browser tab independently shows
  // the right screen. Unknown sub-paths fall back to CAD.
  const pathSeg = routerLocation.pathname.replace(/^\/dispatcher\/?/, "").split("/")[0];
  const tab = PATH_TO_TAB[pathSeg] || "cad";
  const setTab = useCallback((t) => navigate(`/dispatcher/${TAB_TO_PATH[t] || "cad"}`), [navigate]);
  const [jobs, setJobs] = useState([]);
  const [units, setUnits] = useState([]);

  // CAD form state
  const [callType, setCallType] = useState("");
  const [priority, setPriority] = useState(2);
  const [location, setLocation] = useState("");
  const [apt, setApt] = useState("");
  const [phone, setPhone] = useState("");
  const [callerSource, setCallerSource] = useState("");
  const [assignedUnit, setAssignedUnit] = useState("");
  const [locationDataInput, setLocationDataInput] = useState("");
  const [locationDataList, setLocationDataList] = useState([]);
  // Fire-side CIDS (Critical Information Dispatch System) data — mirrors
  // location_data shape but lives in a separate field on the job, swapped
  // in for the `LOCATION DATA` panel when serviceType === "FIRE".
  const [cidsDataInput, setCidsDataInput] = useState("");
  const [cidsDataList, setCidsDataList] = useState([]);
  // "EMS" (default) or "FIRE" — toggles the CAD entry layout (Location
  // Data ↔ CIDS Data) and the call-type catalog.
  const [serviceType, setServiceType] = useState("EMS");
  const [callInfoInput, setCallInfoInput] = useState("");
  const [callInfoList, setCallInfoList] = useState([]);
  const [cadNumber, setCadNumber] = useState(null);
  const [fireJobNumber, setFireJobNumber] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingJobId, setEditingJobId] = useState(null); // when editing an existing call
  // HH:MM:SS (NYC-local) of when this CAD form was first touched. Sent to
  // backend so INITIAL CALL ENTRY stamps with that time (not submit time).
  const [cadFormStartedAt, setCadFormStartedAt] = useState("");

  // GPS state
  const [gpsPositions, setGpsPositions] = useState([]);
  const [confirmedLocation, setConfirmedLocation] = useState("");
  const [locationCoords, setLocationCoords] = useState(null);

  // Admin modal state
  const [adminModal, setAdminModal] = useState(null);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [agencies, setAgencies] = useState([]);
  const [callTypesDB, setCallTypesDB] = useState([]);
  // Fire-side call type catalog. Loaded once on mount; CADEntry uses it
  // when serviceType === "FIRE".
  const [fireCallTypesDB, setFireCallTypesDB] = useState([]);
  // FDNY alarm box id (e.g. "B0370") — auto-resolved by the backend on
  // Fire jobs from lat/lng, but the dispatcher can still override.
  const [box, setBox] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [jobsRes, unitsRes, agenciesRes, ctRes, fireCtRes] = await Promise.all([
        axios.get(`${API}/jobs`),
        axios.get(`${API}/units`),
        axios.get(`${API}/agencies`),
        axios.get(`${API}/calltypes`),
        axios.get(`${API}/fire-calltypes`),
      ]);
      setJobs(jobsRes.data);
      setUnits(unitsRes.data);
      setAgencies(agenciesRes.data);
      setCallTypesDB(ctRes.data);
      setFireCallTypesDB(fireCtRes.data);
    } catch {}
  }, []);

  const fetchGPS = useCallback(() => {
    axios.get(`${API}/gps/positions`).then((r) => {
      const fresh = r.data.filter((p) => p.latitude && p.longitude && p.mapped_unit_id);
      setGpsPositions((prev) => mergeGps(prev, fresh, true));
    }).catch(() => {});
  }, []);

  // ─── Iter 103 — Message ping ───────────────────────────────────────────
  // Short two-note chime (E5 → G5) played once whenever an inbound message
  // arrives for THIS dispatcher while they are not on the Messages tab.
  // Uses Web Audio (no asset), a single shared AudioContext, and a
  // gentle attack/release envelope so it doesn't clip in speakers. Muted
  // silently if the browser blocks autoplay before user interaction —
  // dispatchers always click SIGN IN first so this never bites in
  // practice.
  const msgAudioCtxRef = useRef(null);
  const playMessagePing = useCallback(() => {
    try {
      if (!msgAudioCtxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return;
        msgAudioCtxRef.current = new AC();
      }
      const ctx = msgAudioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.22, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
      gain.connect(ctx.destination);
      const play = (freq, start, dur) => {
        const osc = ctx.createOscillator();
        osc.type = "sine";
        osc.frequency.value = freq;
        osc.connect(gain);
        osc.start(now + start);
        osc.stop(now + start + dur);
      };
      play(659.25, 0, 0.18);   // E5
      play(783.99, 0.14, 0.30); // G5
    } catch { /* autoplay blocked — silently swallow */ }
  }, []);

  // ─── 10-13 Dispatcher audible alert ───
  // Synthesizes a two-note siren chirp (800 Hz → 500 Hz) using Web Audio API
  // and loops it every 3 seconds for as long as ANY unit has emergency_active.
  // No mp3 asset needed — keeps the dispatcher aware even if they're on the
  // CAD Entry tab typing a new call.
  const hasEmergency = units.some((u) => u.emergency_active);
  const emgAudioCtxRef = useRef(null);
  const emgIntervalRef = useRef(null);
  useEffect(() => {
    const playChirp = () => {
      try {
        if (!emgAudioCtxRef.current) {
          const AC = window.AudioContext || window.webkitAudioContext;
          if (!AC) return;
          emgAudioCtxRef.current = new AC();
        }
        const ctx = emgAudioCtxRef.current;
        if (ctx.state === "suspended") ctx.resume().catch(() => {});
        const now = ctx.currentTime;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.25, now);
        gain.connect(ctx.destination);
        // Note 1 (800 Hz, 0.25s)
        const o1 = ctx.createOscillator();
        o1.type = "square";
        o1.frequency.setValueAtTime(800, now);
        o1.connect(gain);
        o1.start(now);
        o1.stop(now + 0.25);
        // Note 2 (500 Hz, 0.25s) after a 50ms gap
        const o2 = ctx.createOscillator();
        o2.type = "square";
        o2.frequency.setValueAtTime(500, now + 0.30);
        o2.connect(gain);
        o2.start(now + 0.30);
        o2.stop(now + 0.55);
      } catch {}
    };

    if (hasEmergency) {
      // Fire once immediately, then every 3s until hasEmergency goes false.
      playChirp();
      emgIntervalRef.current = setInterval(playChirp, 3000);
      return () => {
        if (emgIntervalRef.current) { clearInterval(emgIntervalRef.current); emgIntervalRef.current = null; }
      };
    }
    // Cleanup any outstanding interval if emergencies cleared.
    if (emgIntervalRef.current) { clearInterval(emgIntervalRef.current); emgIntervalRef.current = null; }
  }, [hasEmergency]);

  useEffect(() => {
    fetchGPS();
    // 2s poll (matches router cadence) + WS `gps_update` pushes; mergeGps keeps the newest fix per unit.
    const interval = setInterval(fetchGPS, 2000);
    return () => clearInterval(interval);
  }, [fetchGPS]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [lastMessageEvent, setLastMessageEvent] = useState(null);
  // Iter 96 — Blink the Messages tab red when a new inbound message arrives
  // while the dispatcher is NOT on the messages tab. Clears the moment they
  // click into the tab so the alert doesn't linger after they've seen it.
  const [messagesUnread, setMessagesUnread] = useState(false);

  const handleWsMessage = useCallback((msg) => {
    if (msg.type === "gps_update" && msg.position?.mapped_unit_id) {
      const np = msg.position;
      setGpsPositions((prev) => mergeGps(prev, [np]));
      return;
    }
    if (msg.type === "job_created") {
      setJobs((prev) => [msg.job, ...prev.filter((j) => j.id !== msg.job.id)]);
    } else if (msg.type === "job_updated") {
      setJobs((prev) => prev.map((j) => (j.id === msg.job.id ? msg.job : j)));
    } else if (msg.type === "unit_updated" || msg.type === "unit_created") {
      setUnits((prev) => {
        const exists = prev.find((u) => u.id === (msg.unit?.id || msg.unit?.id));
        if (exists) return prev.map((u) => (u.id === msg.unit.id ? msg.unit : u));
        return [...prev, msg.unit];
      });
    } else if (msg.type === "message_received" || msg.type === "message_read") {
      // Forwarded to the MessageBoard component so it can refresh its
      // visible thread / unread counters in real time.
      setLastMessageEvent({ ...msg, _ts: Date.now() });
      // Iter 96 — Fire the tab blinker only for INBOUND messages addressed
      // to THIS dispatcher, and only while the messages tab is not open.
      // Iter 103 — Also chime once so the dispatcher hears the alert even
      // when their eyes are on the map.
      if (msg.type === "message_received") {
        const m = msg.message || {};
        const myId = user?.user_id || user?.id || "";
        const isForMe = m.recipient_type === "dispatcher" && m.recipient_id === myId;
        // Use latest URL (not stale `tab` closure) — the WS handler is
        // memoized on `user` so `tab` from the outer render can be stale.
        const onMessagesTab = window.location.pathname.startsWith("/dispatcher/messages");
        if (isForMe && !onMessagesTab) {
          setMessagesUnread(true);
          playMessagePing();
        }
      }
    } else if (msg.type === "session_kicked") {
      // Iter 96 — Same-user login on a different terminal. Verify this
      // event was aimed at OUR session token (broadcast is fan-out to
      // every connected client) before dropping to login.
      const myToken = (user && user.session_token) || localStorage.getItem("dispatcher_session_token") || "";
      if (msg.user_id === user?.user_id && msg.session_token === myToken) {
        alert(`You have been signed out — this account signed in on another computer at ${msg.at}.`);
        localStorage.removeItem("dispatcher_user");
        localStorage.removeItem("dispatcher_session_token");
        window.location.href = "/dispatcher";
      }
    }
  }, [user, playMessagePing]);

  const wsConnected = useWebSocket(handleWsMessage);

  // Iter 96 — Clear the Messages tab blink the moment the dispatcher lands
  // on the messages tab (works for click, popout return, and deep-link).
  useEffect(() => {
    if (tab === "messages" && messagesUnread) setMessagesUnread(false);
  }, [tab, messagesUnread]);

  // ─── Dispatcher heartbeat ───
  // Pings the backend every 30s while the dispatcher is on this page so the
  // Message Board's online roster + green-dot indicators stay accurate.
  // Cleanup on unmount calls /auth/dispatcher-logout to drop the entry
  // immediately (without the heartbeat the row would expire after 5 min).
  useEffect(() => {
    if (!user?.user_id && !user?.id) return;
    const me = { user_id: user.user_id || user.id, username: user.username || "", dispatcher_userid: user.dispatcher_userid || "" };
    const ping = () => {
      axios.post(`${API}/auth/dispatcher-heartbeat`, null, { params: me }).catch(() => {});
    };
    ping();
    const t = setInterval(ping, 30000);
    return () => {
      clearInterval(t);
      axios.post(`${API}/auth/dispatcher-logout`, null, { params: { user_id: me.user_id } }).catch(() => {});
    };
  }, [user?.user_id, user?.id, user?.username, user?.dispatcher_userid]);

  const resetForm = () => {
    setCallType("");
    setPriority(2);
    setLocation("");
    setApt("");
    setPhone("");
    setCallerSource("");
    setAssignedUnit("");
    setLocationDataInput("");
    setLocationDataList([]);
    setCidsDataInput("");
    setCidsDataList([]);
    setServiceType("EMS");
    setBox("");
    setCallInfoInput("");
    setCallInfoList([]);
    setConfirmedLocation("");
    setLocationCoords(null);
    setCadNumber(null);
    setFireJobNumber(null);
    setEditingJobId(null);
    setCadFormStartedAt("");
  };

  // Stamp "form started" the first time the dispatcher touches ANY input on
  // the CAD form. Subsequent inputs don't reset it. Cleared by resetForm().
  useEffect(() => {
    if (!cadFormStartedAt && !editingJobId && (callType || location || apt || phone || callerSource || locationDataInput || callInfoInput || locationDataList.length || callInfoList.length)) {
      setCadFormStartedAt(localHMS());
    }
  }, [callType, location, apt, phone, callerSource, locationDataInput, callInfoInput, locationDataList, callInfoList, cadFormStartedAt, editingJobId]);

  // Prefill the CAD form from an existing job and switch to the CAD tab.
  const handleEditJob = useCallback((job) => {
    if (!job) return;
    setEditingJobId(job.id);
    setCallType(job.call_type || "");
    setPriority(job.priority || 2);
    setLocation(job.location || "");
    setApt(job.apt || "");
    setPhone(job.phone || "");
    setCallerSource(job.caller_source || "");
    setAssignedUnit(job.assigned_unit_id || "");
    setLocationDataList(job.location_data || []);
    setCidsDataList(job.cids_data || []);
    setServiceType((job.service_type || "EMS").toUpperCase());
    setBox(job.box || "");
    setCallInfoList(job.call_info || []);
    setConfirmedLocation(job.location || "");
    if (job.lat && job.lng) setLocationCoords([job.lat, job.lng]);
    setCadNumber(job.cad_number || null);
    setFireJobNumber(job.fire_job_number || null);
    setTab("cad");
  }, []);

  /**
   * Normalize an address + apt into the canonical dispatch format:
   *
   *     "1221 FULTON STREET APT 4K, BK"
   *     "STUYVESANT AVENUE AND MACON STREET, BK"
   *
   * Rules:
   *   • Drop trailing state codes (NY) and country ("USA", "US").
   *   • Insert " APT {apt}" between the street portion and the city/borough.
   *   • Convert NYC city/borough names to their 2-letter abbreviations
   *     (Brooklyn→BK, Queens→QN, Manhattan/New York→MN, Bronx→BX, Staten Island→SI).
   *   • Place a COMMA before the borough abbreviation.
   *   • Uppercase the final string to match crew-MDT aesthetics.
   */
  const formatDispatchAddress = (raw, aptValue) => {
    if (!raw) return "";
    const stateRe = /^[A-Z]{2}$/;
    const countryAliases = new Set(["USA", "US", "UNITED STATES", "UNITED STATES OF AMERICA"]);
    const boroughAbbr = {
      "brooklyn": "BK", "queens": "QN", "new york": "MN", "manhattan": "MN",
      "bronx": "BX", "staten island": "SI",
    };
    const parts = raw.split(",").map((p) => p.trim()).filter(Boolean);
    while (parts.length > 1) {
      const last = parts[parts.length - 1];
      if (stateRe.test(last) || countryAliases.has(last.toUpperCase())) {
        parts.pop();
      } else {
        break;
      }
    }
    const cleanApt = (aptValue || "").toString().trim();
    // Try to identify the borough among the tail tokens.
    let borough = "";
    while (parts.length > 1) {
      const last = parts[parts.length - 1].toLowerCase();
      if (boroughAbbr[last]) {
        borough = boroughAbbr[last];
        parts.pop();
        break;
      }
      break;
    }
    // Any remaining segments are the street portion.
    const streetJoined = parts.join(", ");
    const aptSuffix = cleanApt ? ` APT ${cleanApt}` : "";
    if (borough) {
      return `${streetJoined}${aptSuffix}, ${borough}`.toUpperCase();
    }
    // Fallback: no borough found — keep street + apt + last token.
    return `${streetJoined}${aptSuffix}`.toUpperCase();
  };

  const handleSubmit = async () => {
    if (!callType || !location) return;
    setSubmitting(true);
    // Compose the canonical address.
    //
    // BUG FIX (Edit Call false "address updated" popup): when the dispatcher
    // opens Edit Call and submits WITHOUT touching the address, the stored
    // `location` is already in dispatch-canonical form (e.g.
    // "742 BEDFORD AVE APT 4K, BK"). Re-running `formatDispatchAddress` on
    // it (a) strips the already-abbreviated borough "BK" (regex treats it
    // as a state code) and (b) re-appends " APT 4K" producing
    // "...APT 4K APT 4K". The resulting string != the stored one, so the
    // backend flagged `address_changed=true` and broadcast a fake UPDATE
    // popup to every attached MDT.
    //
    // Fix: in edit mode, if the user hasn't touched the address since load,
    // pass through the original stored location verbatim so the backend
    // comparison is a true no-op.
    let cleanLocation;
    if (editingJobId && location.trim() === (confirmedLocation || "").trim() && location) {
      cleanLocation = location;
    } else {
      cleanLocation = formatDispatchAddress(location, apt);
    }
    // Include frontend-resolved coordinates so the backend can skip re-geocoding
    // (saves Stadia credits when the address was picked from the autocomplete dropdown
    // or already resolved on the map).
    const lat = locationCoords ? locationCoords[0] : null;
    const lng = locationCoords ? locationCoords[1] : null;
    try {
      if (editingJobId) {
        // Edit mode: PUT the existing job.
        // Per operator: any stamp the system records of a dispatcher action
        // must use the DISPATCH ID (e.g. "D902"), not the full human name.
        await axios.put(`${API}/jobs/${editingJobId}`, {
          dispatcher: user.dispatcher_userid || user.username || "",
          call_type: callType,
          priority,
          location: cleanLocation,
          apt,
          phone,
          caller_source: callerSource,
          location_data: locationDataList,
          cids_data: cidsDataList,
          service_type: serviceType,
          box,
          call_info: callInfoList,
          lat, lng,
        });
        resetForm();
        setTab("board");
      } else {
        const res = await axios.post(`${API}/jobs`, {
          call_type: callType,
          priority,
          location: cleanLocation,
          apt,
          phone,
          caller_source: callerSource,
          location_data: locationDataList,
          cids_data: cidsDataList,
          service_type: serviceType,
          box,
          call_info: callInfoList,
          assigned_unit_id: assignedUnit || null,
          agency: (user.agencies || []).join(", "),
          lat, lng,
          form_started_at: cadFormStartedAt || localHMS(),
        });
        setCadNumber(res.data.cad_number);
        setFireJobNumber(res.data.fire_job_number || null);
        resetForm();
        setCadNumber(res.data.cad_number);
        setFireJobNumber(res.data.fire_job_number || null);
      }
    } catch (err) {
      console.error("Failed to save job:", err);
    }
    setSubmitting(false);
  };

  const handleLogout = () => {
    onLogout();
    navigate("/");
  };

  const activeJobs = jobs.filter((j) => j.status !== "completed" && j.status !== "cancelled");
  const callTypes = ["Medical Emergency", "Cardiac Arrest", "Trauma", "MVA", "Fire", "Difficulty Breathing", "Fall", "Stroke", "Allergic Reaction", "Other"];
  const callerSources = ["9 LINE", "PD", "EMS", "FD", "FLAGGED DOWN", "REQUEST FOR TRANSPORT"];

  return (
    <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Iter 98 — Training sandbox banner. Rendered ONLY when the logged-in
          dispatcher entered demo mode. Yellow + high-contrast so nobody
          confuses this session with live dispatch. Includes a Reset button
          that wipes and re-seeds the demo dataset for the trainee's agency. */}
      {user.demo_mode && (
        <div data-testid="demo-banner"
          style={{
            background: "repeating-linear-gradient(45deg,#FFCC00,#FFCC00 20px,#000 20px,#000 40px)",
            padding: "2px 0",
          }}
        >
          <div style={{
            background: "#FFCC00", color: "#000", padding: "6px 16px",
            fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 800,
            letterSpacing: 2, fontSize: 13, display: "flex",
            alignItems: "center", justifyContent: "space-between", gap: 12,
          }}>
            <span>
              ⚠ DEMO — TRAINING MODE · isolated sandbox · nothing here affects live dispatch
              {user.agencies?.length ? (
                <span style={{ marginLeft: 12, opacity: 0.8, fontWeight: 600 }}>
                  Agency: {user.agencies[0]}
                </span>
              ) : null}
            </span>
            <button data-testid="demo-reset-btn"
              onClick={async () => {
                if (!window.confirm("Reset the demo? All training jobs, unit history, and messages for this agency will be wiped and a fresh 5-unit roster will be seeded.")) return;
                try {
                  await axios.post(`${API}/demo/reset`, { agency: user.agencies?.[0] || "" });
                  alert("Demo reset — fresh roster seeded. Refreshing…");
                  window.location.reload();
                } catch (e) {
                  alert("Demo reset failed: " + (e?.response?.data?.error || e.message));
                }
              }}
              style={{
                background: "#000", color: "#FFCC00", border: "1px solid #000",
                padding: "4px 14px", fontFamily: "'IBM Plex Sans', sans-serif",
                fontWeight: 700, fontSize: 12, letterSpacing: 1,
                cursor: "pointer", borderRadius: 3,
              }}
            >
              RESET DEMO
            </button>
          </div>
        </div>
      )}
      {/* Top Nav */}
      <div className="top-nav">
        <div className="nav-left">
          <span className="nav-brand">
            <Ambulance size={14} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
            Volunteer EMS Comms
          </span>
          <div className="nav-tabs">
            <TabLink tabId="cad" active={tab === "cad"} onActivate={setTab}>CAD Entry</TabLink>
            <TabLink tabId="board" active={tab === "board"} onActivate={setTab}>Dispatch Board</TabLink>
            <TabLink tabId="classroom" active={tab === "classroom"} onActivate={setTab}>External Display</TabLink>
            <TabLink tabId="gps" active={tab === "gps"} onActivate={setTab}>GPS</TabLink>
            <TabLink tabId="vacrcc" active={tab === "vacrcc"} onActivate={setTab}>VAC/VFD RCC</TabLink>
            <TabLink tabId="messages" active={tab === "messages"} onActivate={setTab} blink={messagesUnread}>Messages</TabLink>
            <TabLink tabId="diversions" active={tab === "diversions"} onActivate={setTab}>Diversions</TabLink>
          </div>
        </div>
        <div className="nav-right">
          <span data-testid="user-info">
            {/* Operator spec: header reads "James Pointer (D002)" — full
                human name with the dispatch ID in parens. No agency list,
                no certification suffix, no role label. */}
            {user.username}
            {user.dispatcher_userid ? (
              <span style={{ marginLeft: 6, fontSize: 12, color: "#7AB8E8", fontWeight: 600 }}>
                ({user.dispatcher_userid})
              </span>
            ) : null}
          </span>
          <span data-testid="ws-status">
            {wsConnected ? <Wifi size={12} color="#00CC66" /> : <WifiOff size={12} color="#FF3333" />}
          </span>
          <span className={`ws-indicator ${wsConnected ? "connected" : "disconnected"}`} />
          <button data-testid="logout-btn" className="logout-btn" onClick={handleLogout}>
            <LogOut size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
            Logout
          </button>
          {/* Iter 92 — Unit History quick-access button. Previously this was
              buried under Admin → Unit History; operator requested it on
              the banner for faster recall during active dispatch. */}
          <button data-testid="banner-unit-history-btn" className="logout-btn"
            onClick={() => setAdminModal("unithistory")}
            style={{ borderColor: "#00BFFF", color: "#00BFFF" }}>
            <History size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
            Unit History
          </button>
          {/* Admin Dropdown */}
          <div style={{ position: "relative" }}>
            <button data-testid="admin-menu-btn" className="logout-btn" onClick={() => setAdminMenuOpen(!adminMenuOpen)}
              style={{ borderColor: adminMenuOpen ? "#00BFFF" : undefined, color: adminMenuOpen ? "#00BFFF" : undefined }}>
              <Settings size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
              Admin
            </button>
            {adminMenuOpen && (
              <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, background: "#111", border: "1px solid #333", zIndex: 500, minWidth: 160 }}>
                {(() => {
                  // Permission-aware admin menu. DISPATCHER-only users (no
                  // AGENCY ADMIN / SUPERVISOR permission) only see Unit History.
                  const perms = (user?.permissions || []).map((p) => (p || "").toUpperCase());
                  const isBuiltInAdmin = user?.dispatcher_userid === "admin";
                  const canEdit = isBuiltInAdmin || perms.some((p) => p === "AGENCY ADMIN" || p === "SUPERVISOR" || p === "ADMIN");
                  const all = [
                    { id: "user", label: "Add/Edit User", editOnly: true },
                    { id: "agency", label: "Add/Edit Agency", editOnly: true },
                    { id: "hospital", label: "Add/Edit Hospital", editOnly: true },
                    { id: "unit", label: "Add/Edit Unit", editOnly: true },
                    { id: "calltypes", label: "Call Types (EMS)", editOnly: true },
                    { id: "fire-calltypes", label: "Call Types (Fire)", editOnly: true },
                    { id: "calltype-mappings", label: "Call Type Mappings (EMS ↔ Fire)", editOnly: true },
                    { id: "fire-mdt-bindings", label: "Fire MDT Bindings", editOnly: true },
                    { id: "csl", label: "Add/Edit CSL", editOnly: true },
                    { id: "kml", label: "KML Boundaries", editOnly: true },
                    { id: "cad-counters", label: "CAD Number Settings", editOnly: true },
                    { id: "gps-devices", label: "GPS Tracking (Devices)", editOnly: true },
                    { id: "unithistory", label: "Unit History" },
                    { id: "reports", label: "Reports" },
                    { id: "force-logoff", label: "Force Log Off Units…", editOnly: true, danger: true },
                  ];
                  return all.filter((i) => canEdit || !i.editOnly);
                })().map((item) => (
                  <div key={item.id} data-testid={`admin-open-${item.id}`}
                    onClick={async () => {
                      setAdminMenuOpen(false);
                      if (item.id === "force-logoff") {
                        // Open the picker modal — operator can choose all
                        // or a subset of currently logged-in units.
                        setAdminModal("force-logoff");
                        return;
                      }
                      setAdminModal(item.id);
                    }}
                    style={{ padding: "8px 14px", cursor: "pointer", fontSize: 12, fontFamily: "'Manrope', sans-serif", fontWeight: 600, color: item.danger ? "#FF5555" : "#CCC", borderBottom: "1px solid #222" }}
                    onMouseEnter={(e) => { e.target.style.background = item.danger ? "#2a0a0a" : "#1a1a1a"; e.target.style.color = item.danger ? "#FF8888" : "#00BFFF"; }}
                    onMouseLeave={(e) => { e.target.style.background = "transparent"; e.target.style.color = item.danger ? "#FF5555" : "#CCC"; }}>
                    {item.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Admin Modals */}
      {adminModal === "user" && <UserModal onClose={() => setAdminModal(null)} onSaved={fetchData} agencies={agencies} currentUser={user} />}
      {adminModal === "agency" && <AgencyModal onClose={() => setAdminModal(null)} onSaved={fetchData} />}
      {adminModal === "hospital" && <HospitalModal onClose={() => setAdminModal(null)} onSaved={fetchData} />}
      {adminModal === "unit" && <UnitModal onClose={() => setAdminModal(null)} onSaved={fetchData} />}
      {adminModal === "calltypes" && <CallTypesModal onClose={() => setAdminModal(null)} onSaved={fetchData} />}
      {adminModal === "fire-calltypes" && <FireCallTypesModal onClose={() => setAdminModal(null)} onSaved={fetchData} />}
      {adminModal === "calltype-mappings" && <CallTypeMappingsModal onClose={() => setAdminModal(null)} />}
      {adminModal === "fire-mdt-bindings" && <FireMDTBindingsModal onClose={() => setAdminModal(null)} />}
      {adminModal === "csl" && <CSLModal onClose={() => setAdminModal(null)} onSaved={fetchData} />}
      {adminModal === "kml" && <KMLBoundaryModal onClose={() => setAdminModal(null)} onSaved={fetchData} />}
      {adminModal === "cad-counters" && <CadCountersModal onClose={() => setAdminModal(null)} agencies={agencies} />}
      {adminModal === "gps-devices" && <GPSDevicesModal onClose={() => setAdminModal(null)} />}
      {adminModal === "unithistory" && <UnitHistoryModal onClose={() => setAdminModal(null)} />}
      {adminModal === "reports" && <ReportsModal user={user} onClose={() => setAdminModal(null)} />}
      {adminModal === "force-logoff" && <ForceLogoffModal onClose={() => setAdminModal(null)} onDone={fetchData} />}

      {/* Content */}
      {tab === "cad" ? (
        <CADEntry
          callType={callType} setCallType={setCallType}
          priority={priority} setPriority={setPriority}
          location={location} setLocation={setLocation}
          apt={apt} setApt={setApt}
          phone={phone} setPhone={setPhone}
          callerSource={callerSource} setCallerSource={setCallerSource}
          assignedUnit={assignedUnit} setAssignedUnit={setAssignedUnit}
          locationDataInput={locationDataInput} setLocationDataInput={setLocationDataInput}
          locationDataList={locationDataList} setLocationDataList={setLocationDataList}
          cidsDataInput={cidsDataInput} setCidsDataInput={setCidsDataInput}
          cidsDataList={cidsDataList} setCidsDataList={setCidsDataList}
          serviceType={serviceType} setServiceType={setServiceType}
          box={box} setBox={setBox}
          callInfoInput={callInfoInput} setCallInfoInput={setCallInfoInput}
          callInfoList={callInfoList} setCallInfoList={setCallInfoList}
          cadNumber={cadNumber}
          fireJobNumber={fireJobNumber}
          submitting={submitting}
          handleSubmit={handleSubmit}
          units={units}
          jobs={activeJobs}
          dispatchAgencies={user.agencies || []}
          callTypes={callTypes}
          callerSources={callerSources}
          confirmedLocation={confirmedLocation} setConfirmedLocation={setConfirmedLocation}
          locationCoords={locationCoords} setLocationCoords={setLocationCoords}
          gpsPositions={gpsPositions}
          callTypesDB={callTypesDB}
          fireCallTypesDB={fireCallTypesDB}
          editingJobId={editingJobId}
        />
      ) : tab === "board" ? (
        <DispatchBoard
          units={units}
          jobs={activeJobs}
          dispatchAgencies={user.agencies || []}
          agencies={agencies}
          locationCoords={locationCoords}
          callTypesDB={callTypesDB}
          gpsPositions={gpsPositions}
          fetchData={fetchData}
          dispatcherId={user.dispatcher_userid || user.username || ""}
          onEditJob={handleEditJob}
        />
      ) : tab === "classroom" ? (
        <ClassroomScreen
          units={units}
          jobs={activeJobs}
          gpsPositions={gpsPositions}
          agencies={user.agencies || []}
        />
      ) : tab === "vacrcc" ? (
        <VACRCCTab
          units={units}
          agencies={agencies}
        />
      ) : tab === "messages" ? (
        <MessageBoard
          user={user}
          units={units}
          lastMessageEvent={lastMessageEvent}
        />
      ) : tab === "diversions" ? (
        <DiversionsTab />
      ) : (
        <GPSMapScreen gpsPositions={gpsPositions} />
      )}
    </div>
  );
}
